import { createReducer, on } from '@ngrx/store';
import { OnboardingState, initialOnboardingState } from './onboarding.state';
import * as OnboardingActions from './onboarding.actions';
import { resetState } from '../global/global.actions';

export const onboardingReducer = createReducer(
  initialOnboardingState,

  // ============================================
  // Session Init Reducers
  // ============================================
  on(OnboardingActions.initOnboardingSession, (state): OnboardingState => ({
    ...state,
    session: {
      ...state.session,
      isLoading: true,
      error: null
    }
  })),

  on(OnboardingActions.initOnboardingSessionSuccess, (state, { sessionId, currentStep, expiresAt }): OnboardingState => ({
    ...state,
    session: {
      sessionId,
      currentStep,
      expiresAt,
      isLoading: false,
      error: null
    },
    stepData: {}
  })),

  on(OnboardingActions.initOnboardingSessionFailure, (state, { error }): OnboardingState => ({
    ...state,
    session: {
      ...state.session,
      isLoading: false,
      error: error?.message || error || 'Failed to initialize session'
    }
  })),

  // ============================================
  // Session Resume Reducers
  // ============================================
  on(OnboardingActions.resumeOnboardingSession, (state): OnboardingState => ({
    ...state,
    session: {
      ...state.session,
      isLoading: true,
      error: null
    }
  })),

  on(OnboardingActions.resumeOnboardingSessionSuccess, (state, {
    sessionId,
    currentStep,
    expiresAt,
    stepData,
    extractionStatus,
    productDescription,
    companyName,
    benefits,
    pains
  }): OnboardingState => ({
    ...state,
    session: {
      sessionId,
      currentStep,
      expiresAt,
      isLoading: false,
      error: null
    },
    stepData: stepData || {},
    extraction: {
      ...state.extraction,
      status: extractionStatus || null,
      productDescription: productDescription || null,
      companyName: companyName || null,
      benefits: benefits || null,
      pains: pains || null
    }
  })),

  on(OnboardingActions.resumeOnboardingSessionFailure, (state, { error }): OnboardingState => ({
    ...state,
    session: {
      ...state.session,
      isLoading: false,
      error: error?.message || error || 'Failed to resume session'
    }
  })),

  // ============================================
  // Update Step Reducers
  // ============================================
  on(OnboardingActions.updateOnboardingStep, (state): OnboardingState => ({
    ...state,
    updateStep: {
      isLoading: true,
      success: false,
      error: null
    }
  })),

  on(OnboardingActions.updateOnboardingStepSuccess, (state, { step, data, stepData }): OnboardingState => ({
    ...state,
    session: {
      ...state.session,
      currentStep: step
    },
    stepData: {
      ...state.stepData,
      ...stepData,
      [`step${step}`]: data
    },
    updateStep: {
      isLoading: false,
      success: true,
      error: null
    }
  })),

  on(OnboardingActions.updateOnboardingStepFailure, (state, { error }): OnboardingState => ({
    ...state,
    updateStep: {
      isLoading: false,
      success: false,
      error: error?.message || error || 'Failed to save step'
    }
  })),

  on(OnboardingActions.resetUpdateStepState, (state): OnboardingState => ({
    ...state,
    updateStep: {
      isLoading: false,
      success: false,
      error: null
    }
  })),

  // ============================================
  // Extract Product Reducers
  // ============================================
  on(OnboardingActions.extractProduct, (state): OnboardingState => ({
    ...state,
    extraction: {
      ...state.extraction,
      status: 'pending',
      isLoading: true,
      error: null
    }
  })),

  on(OnboardingActions.extractProductSuccess, (state, { extractionStatus }): OnboardingState => ({
    ...state,
    extraction: {
      ...state.extraction,
      status: extractionStatus,
      isLoading: true
    }
  })),

  on(OnboardingActions.extractProductFailure, (state, { error }): OnboardingState => ({
    ...state,
    extraction: {
      ...state.extraction,
      status: 'failed',
      isLoading: false,
      error: error?.message || error || 'Failed to extract product data'
    }
  })),

  // ============================================
  // Extraction Status Polling Reducers
  // ============================================
  on(OnboardingActions.extractionStillProcessing, (state, { extractionStatus }): OnboardingState => ({
    ...state,
    extraction: {
      ...state.extraction,
      status: extractionStatus,
      isLoading: true
    }
  })),

  on(OnboardingActions.pollExtractionStatusSuccess, (state, {
    extractionStatus,
    extractionSource,
    productDescription,
    companyName,
    benefits,
    pains
  }): OnboardingState => ({
    ...state,
    extraction: {
      status: extractionStatus,
      extractionSource: extractionSource || state.extraction.extractionSource,
      productDescription: productDescription || state.extraction.productDescription,
      companyName: companyName || state.extraction.companyName,
      benefits: benefits || state.extraction.benefits,
      pains: pains || state.extraction.pains,
      isLoading: false,
      error: null
    }
  })),

  on(OnboardingActions.pollExtractionStatusFailure, (state, { error }): OnboardingState => ({
    ...state,
    extraction: {
      ...state.extraction,
      status: 'failed',
      isLoading: false,
      error: error?.message || error || 'Failed to check extraction status'
    }
  })),

  on(OnboardingActions.extractionTimeout, (state): OnboardingState => ({
    ...state,
    extraction: {
      ...state.extraction,
      status: 'failed',
      isLoading: false,
      error: 'Extraction timed out'
    }
  })),

  // ============================================
  // Complete Onboarding Reducers
  // ============================================
  on(OnboardingActions.completeOnboarding, (state): OnboardingState => ({
    ...state,
    complete: {
      isLoading: true,
      success: false,
      result: null,
      error: null
    }
  })),

  on(OnboardingActions.completeOnboardingSuccess, (state, { result }): OnboardingState => ({
    ...state,
    complete: {
      isLoading: false,
      success: true,
      result,
      error: null
    },
    leads: {
      ...state.leads,
      generationStatus: result.lead_generation_status ?? state.leads.generationStatus
    }
  })),

  on(OnboardingActions.completeOnboardingFailure, (state, { error }): OnboardingState => ({
    ...state,
    complete: {
      isLoading: false,
      success: false,
      result: null,
      error: error?.message || error || 'Failed to complete onboarding'
    }
  })),

  // ============================================
  // WebSocket Lead Reducers
  // ============================================
  on(OnboardingActions.startListeningForLeads, (state): OnboardingState => ({
    ...state,
    leads: {
      ...state.leads,
      isListening: true
    }
  })),

  on(OnboardingActions.stopListeningForLeads, (state): OnboardingState => ({
    ...state,
    leads: {
      ...state.leads,
      isListening: false
    }
  })),

  on(OnboardingActions.leadGenerated, (state, { lead, generationStatus }): OnboardingState => ({
    ...state,
    leads: {
      ...state.leads,
      items: [...state.leads.items, lead],
      generationStatus: generationStatus ?? state.leads.generationStatus
    }
  })),

  // ============================================
  // Local State Management Reducers
  // ============================================
  on(OnboardingActions.setCurrentStep, (state, { step }): OnboardingState => ({
    ...state,
    session: {
      ...state.session,
      currentStep: step
    }
  })),

  on(OnboardingActions.setStepData, (state, { stepKey, data }): OnboardingState => ({
    ...state,
    stepData: {
      ...state.stepData,
      [stepKey]: data
    }
  })),

  on(OnboardingActions.resetOnboardingState, (): OnboardingState => initialOnboardingState),

  // Reset on global logout
  on(resetState, (): OnboardingState => initialOnboardingState)
);

export { OnboardingState };
